import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getUsers, createUser, updateUser, resetUserPassword, deleteUser,
  getContactInfoTemplates, deleteContactInfoTemplate,
  getIndustries, createIndustry, updateIndustry, deleteIndustry,
  getIndustryGroups,
  getProfilingTemplates, deleteProfilingTemplate,
  getProductBriefs, deleteProductBrief, setActiveProductBrief,
  getSignals, deleteSignal,
  getAiSettings, updateAiSettings, testAiProvider, testHunterIo,
  getAiPromptTemplates, deleteAiPromptTemplate,
  getInteractionTemplates, deleteInteractionTemplate, createTemplateVersion,
  getEmailSettings, updateEmailSettings, testEmailConnection,
  getSystemEmailTemplates,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import DemoBookingsPage from './DemoBookingsPage';
import AppointmentTypesPage from './AppointmentTypesPage';
import LogsPage from './LogsPage';
import IndustryGroupsTab from '../components/management/IndustryGroupsTab';
import {
  Box, Typography, Button, Paper, Alert, Chip, TextField, MenuItem,
  Grid, Stack, Divider, DialogActions, Switch, FormControlLabel, FormGroup, Checkbox, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
  Key as KeyIcon,
} from '@mui/icons-material';

const ROLES = ['Admin', 'User'];

export default function ManagementPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'users';
  const [error, setError] = useState('');

  // Users
  const [users, setUsers] = useState([]);
  const [userModal, setUserModal] = useState(null);
  const [userForm, setUserForm] = useState({});
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Field Templates
  const [templates, setTemplates] = useState([]);

  // Industries
  const [industryList, setIndustryList] = useState([]);
  const [industryModal, setIndustryModal] = useState(null);
  const [industryForm, setIndustryForm] = useState({});
  const [industryGroupOptions, setIndustryGroupOptions] = useState([]);

  // Profiling Templates
  const [profilingTemplates, setProfilingTemplates] = useState([]);

  // Product Briefs (Marketing Brain)
  const [productBriefs, setProductBriefs] = useState([]);

  // Signals
  const [signals, setSignals] = useState([]);

  // AI Prompt Templates
  const [aiPromptTemplates, setAiPromptTemplates] = useState([]);

  // AI Settings
  const [aiSettings, setAiSettings] = useState(null);
  const [aiForm, setAiForm] = useState({});
  const [defaultContactProviders, setDefaultContactProviders] = useState([]);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiSaving, setAiSaving] = useState(false);

  // Interaction Templates
  const [interactionTemplates, setInteractionTemplates] = useState([]);

  // System Email Templates (transactional — demo/exchange)
  const [systemEmailTemplates, setSystemEmailTemplates] = useState([]);

  // Email Settings — multi-provider (SMTP + Brevo)
  const [emailProviders, setEmailProviders] = useState([]);
  const [activeProvider, setActiveProvider] = useState('smtp');
  const [smtpForm, setSmtpForm] = useState({
    host: '', port: 587, username: '', password: '',
    useSsl: true, fromAddress: '', fromName: ''
  });
  const [brevoForm, setBrevoForm] = useState({
    apiKey: '', fromAddress: '', fromName: ''
  });
  const [emailSavingProvider, setEmailSavingProvider] = useState(null);
  const [emailTestingProvider, setEmailTestingProvider] = useState(null);
  const [emailSettingsTestResult, setEmailSettingsTestResult] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await getUsers();
      setUsers(data);
    } catch { setError('Failed to load users'); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await getContactInfoTemplates();
      setTemplates(data);
    } catch { setError('Failed to load field templates'); }
  }, []);

  const fetchIndustries = useCallback(async () => {
    try {
      const { data } = await getIndustries();
      setIndustryList(data);
    } catch { setError('Failed to load industries'); }
  }, []);

  const fetchIndustryGroupOptions = useCallback(async () => {
    try {
      const { data } = await getIndustryGroups();
      setIndustryGroupOptions(data || []);
    } catch { /* admin only */ }
  }, []);

  const fetchProfilingTemplates = useCallback(async () => {
    try {
      const { data } = await getProfilingTemplates();
      setProfilingTemplates(data);
    } catch { setError('Failed to load profiling templates'); }
  }, []);

  const fetchProductBriefs = useCallback(async () => {
    try {
      const { data } = await getProductBriefs();
      setProductBriefs(data);
    } catch { setError('Failed to load product briefs'); }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const { data } = await getSignals();
      setSignals(data);
    } catch { setError('Failed to load signals'); }
  }, []);

  const fetchAiPromptTemplates = useCallback(async () => {
    try {
      const { data } = await getAiPromptTemplates();
      setAiPromptTemplates(data);
    } catch { setError('Failed to load AI prompt templates'); }
  }, []);

  const fetchAiSettings = useCallback(async () => {
    try {
      const { data } = await getAiSettings();
      setAiSettings(data);
      // Initialize per-provider form state
      const formState = {};
      if (data.providers?.length > 0) {
        data.providers.forEach(p => {
          formState[p.name] = { apiKey: p.apiKey || '', model: p.model || '', maxTokens: p.maxTokens || 4096, availableModels: p.availableModels || [], enrichmentOrder: p.enrichmentOrder || 0 };
        });
      }
      setAiForm(formState);
      setDefaultContactProviders(data.defaultContactProviders || []);
    } catch { /* admin only - may fail for non-admins */ }
  }, []);

  const fetchInteractionTemplates = useCallback(async () => {
    try {
      const { data } = await getInteractionTemplates();
      setInteractionTemplates(data || []);
    } catch { setError('Failed to load interaction templates'); }
  }, []);

  const fetchSystemEmailTemplates = useCallback(async () => {
    try {
      const { data } = await getSystemEmailTemplates();
      setSystemEmailTemplates(data || []);
    } catch { /* admin only */ }
  }, []);

  const fetchEmailSettings = useCallback(async () => {
    try {
      const { data } = await getEmailSettings();
      const list = data || [];
      setEmailProviders(list);

      const smtp = list.find(c => (c.providerName || '').toLowerCase() === 'smtp');
      if (smtp) {
        setSmtpForm({
          host: smtp.host || '', port: smtp.port || 587,
          username: smtp.username || '', password: '',
          useSsl: smtp.useSsl !== false,
          fromAddress: smtp.fromAddress || '', fromName: smtp.fromName || ''
        });
      }

      const brevo = list.find(c => (c.providerName || '').toLowerCase() === 'brevo');
      if (brevo) {
        setBrevoForm({
          apiKey: '',
          fromAddress: brevo.fromAddress || '', fromName: brevo.fromName || ''
        });
      }

      const active = list.find(c => c.isDefault);
      if (active) setActiveProvider((active.providerName || 'smtp').toLowerCase());
    } catch { /* admin only */ }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchTemplates();
    fetchIndustries();
    fetchIndustryGroupOptions();
    fetchProfilingTemplates();
    fetchProductBriefs();
    fetchSignals();
    fetchAiPromptTemplates();
    fetchAiSettings();
    fetchInteractionTemplates();
    fetchEmailSettings();
    fetchSystemEmailTemplates();
  }, [fetchUsers, fetchTemplates, fetchIndustries, fetchIndustryGroupOptions, fetchProfilingTemplates, fetchProductBriefs, fetchSignals, fetchAiPromptTemplates, fetchAiSettings, fetchInteractionTemplates, fetchEmailSettings, fetchSystemEmailTemplates]);

  // ---- User handlers ----
  const openAddUser = () => {
    setUserForm({ username: '', email: '', password: '', fullName: '', jobTitle: '', emailSignature: '', role: 'User' });
    setUserModal('add');
  };

  const openEditUser = (u) => {
    setUserForm({ username: u.username, email: u.email, fullName: u.fullName, jobTitle: u.jobTitle || '', emailSignature: u.emailSignature || '', role: u.role, isActive: u.isActive });
    setUserModal(u);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (userModal === 'add') {
        await createUser(userForm);
      } else {
        await updateUser(userModal.id, userForm);
      }
      setUserModal(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete user', message: 'Delete this user?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteUser(id);
      fetchUsers();
    } catch { setError('Failed to delete user'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await resetUserPassword(passwordModal.id, { newPassword });
      setPasswordModal(null);
      setNewPassword('');
    } catch { setError('Failed to reset password'); }
  };

  // ---- Field Template handlers ----
  const handleDeleteTemplate = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete field template', message: 'Delete this field template? This will remove it from all contacts.', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteContactInfoTemplate(id);
      fetchTemplates();
    } catch { setError('Failed to delete field template'); }
  };

  // ---- Industry handlers ----
  const openAddIndustry = () => {
    setIndustryForm({ name: '', industryGroupId: null });
    setIndustryModal('add');
  };

  const openEditIndustry = (ind) => {
    setIndustryForm({ name: ind.name, industryGroupId: ind.industryGroupId ?? null });
    setIndustryModal(ind);
  };

  const handleSaveIndustry = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (industryModal === 'add') {
        await createIndustry(industryForm);
      } else {
        await updateIndustry(industryModal.id, industryForm);
      }
      setIndustryModal(null);
      fetchIndustries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save industry');
    }
  };

  const handleDeleteIndustry = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete industry', message: 'Delete this industry?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteIndustry(id);
      fetchIndustries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete industry');
    }
  };

  // ---- Profiling Template handlers ----
  const handleDeleteProfiling = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete profiling template', message: 'Delete this profiling template?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteProfilingTemplate(id);
      fetchProfilingTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete profiling template');
    }
  };

  // ---- Product Brief (Marketing Brain) handlers ----

  const handleDeleteBrief = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete product brief', message: 'Delete this product brief?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteProductBrief(id);
      fetchProductBriefs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete product brief');
    }
  };

  const handleSetActiveBrief = async (id) => {
    try {
      await setActiveProductBrief(id);
      fetchProductBriefs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set active brief');
    }
  };

  // ---- Signal handlers ----
  const handleDeleteSignal = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete signal', message: 'Delete this signal?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteSignal(id);
      fetchSignals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete signal');
    }
  };

  // ---- AI Settings handlers ----
  const handleSaveAiSettings = async (e) => {
    e.preventDefault();
    setError('');
    setAiSaving(true);
    try {
      const providers = Object.entries(aiForm).map(([name, data]) => ({
        name,
        apiKey: data.apiKey,
        model: data.model || '',
        maxTokens: parseInt(data.maxTokens) || 4096,
        enrichmentOrder: parseInt(data.enrichmentOrder) || 0
      }));
      await updateAiSettings({
        defaultProvider: aiSettings?.defaultProvider || 'gemini',
        defaultContactProviders,
        providers
      });
      setAiTestResult({ success: true, message: 'Settings saved successfully' });
      fetchAiSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save AI settings');
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestAiProvider = async (providerName) => {
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const { data } = await testAiProvider({ providerName });
      setAiTestResult({ success: true, message: data.message });
    } catch (err) {
      setAiTestResult({ success: false, message: err.response?.data?.message || 'Connection test failed' });
    } finally {
      setAiTesting(false);
    }
  };

  const handleTestHunterIo = async () => {
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const { data } = await testHunterIo();
      setAiTestResult({ success: true, message: data.message });
    } catch (err) {
      setAiTestResult({ success: false, message: err.response?.data?.message || 'Hunter.io test failed' });
    } finally {
      setAiTesting(false);
    }
  };

  // ---- Interaction Template handlers ----
  const handleDeleteInteractionTemplate = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete interaction template', message: 'Delete this interaction template?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteInteractionTemplate(id);
      fetchInteractionTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete interaction template');
    }
  };

  const handleNewVersion = async (id) => {
    try {
      const { data } = await createTemplateVersion(id);
      navigate(`/management/interaction-templates/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create new version');
    }
  };

  // ---- Email Settings handlers ----
  const buildSmtpPayload = (active) => ({
    providerName: 'smtp',
    displayName: 'SMTP',
    host: smtpForm.host,
    port: parseInt(smtpForm.port) || 587,
    username: smtpForm.username,
    password: smtpForm.password,
    useSsl: smtpForm.useSsl,
    fromAddress: smtpForm.fromAddress,
    fromName: smtpForm.fromName,
    isDefault: active === 'smtp',
    isActive: true,
  });

  const buildBrevoPayload = (active) => ({
    providerName: 'brevo',
    displayName: 'Brevo',
    host: '',
    port: 0,
    apiKey: brevoForm.apiKey,
    useSsl: true,
    fromAddress: brevoForm.fromAddress,
    fromName: brevoForm.fromName,
    isDefault: active === 'brevo',
    isActive: true,
  });

  const handleSaveProvider = async (providerName) => {
    setEmailSavingProvider(providerName);
    setError('');
    setEmailSettingsTestResult(null);
    try {
      const payload = providerName === 'brevo'
        ? buildBrevoPayload(activeProvider)
        : buildSmtpPayload(activeProvider);
      await updateEmailSettings([payload]);
      setEmailSettingsTestResult({ success: true, message: `${providerName.toUpperCase()} settings saved` });
      fetchEmailSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save email settings');
    } finally {
      setEmailSavingProvider(null);
    }
  };

  const handleChangeActiveProvider = async (newActive) => {
    const prev = activeProvider;
    setActiveProvider(newActive);
    setError('');
    try {
      const payloads = [];
      if (emailProviders.some(p => (p.providerName || '').toLowerCase() === 'smtp')) {
        payloads.push(buildSmtpPayload(newActive));
      }
      if (emailProviders.some(p => (p.providerName || '').toLowerCase() === 'brevo')) {
        payloads.push(buildBrevoPayload(newActive));
      }
      if (payloads.length === 0) return;
      await updateEmailSettings(payloads);
      setEmailSettingsTestResult({ success: true, message: `Active provider set to ${newActive.toUpperCase()}` });
      fetchEmailSettings();
    } catch (err) {
      setActiveProvider(prev);
      setError(err.response?.data?.message || 'Failed to change active provider');
    }
  };

  const handleTestProvider = async (providerName) => {
    setEmailTestingProvider(providerName);
    setEmailSettingsTestResult(null);
    try {
      const { data } = await testEmailConnection(providerName);
      setEmailSettingsTestResult({
        success: data.success,
        message: data.success
          ? `${providerName.toUpperCase()} connection OK`
          : `${providerName.toUpperCase()} connection failed`
      });
    } catch (err) {
      setEmailSettingsTestResult({
        success: false,
        message: err.response?.data?.message || err.response?.data || 'Connection test failed'
      });
    } finally {
      setEmailTestingProvider(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  // ---- Column definitions ----

  const userColumns = [
    { key: 'username', header: 'Username', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'fullName', header: 'Full Name', hideOnMobile: true, render: (v) => v || '-' },
    { key: 'email', header: 'Email', hideOnMobile: true },
    { key: 'role', header: 'Role', hideOnMobile: true, render: (v) => <Chip label={v} size="small" color={v === 'Admin' ? 'primary' : 'default'} variant={v === 'Admin' ? 'filled' : 'outlined'} /> },
    { key: 'isActive', header: 'Status', hideOnMobile: true, render: (v) => <Chip label={v ? 'Active' : 'Inactive'} size="small" color={v ? 'success' : 'default'} variant={v ? 'filled' : 'outlined'} /> },
    { key: 'createdAt', header: 'Created', hideOnMobile: true, hideOnTablet: true, render: (v) => formatDate(v) },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEditUser(row)}>Edit</Button>
          <Button variant="outlined" size="small" startIcon={<LockResetIcon />} onClick={() => { setPasswordModal(row); setNewPassword(''); }}>Password</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteUser(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const industryColumns = [
    { key: 'name', header: 'Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    {
      key: 'industryGroupName', header: 'Group', hideOnMobile: true, render: (v, row) => v ? (
        <Chip
          size="small"
          label={v}
          variant="outlined"
          sx={row.industryGroupColor ? {
            borderColor: row.industryGroupColor,
            color: row.industryGroupColor,
          } : undefined}
        />
      ) : <Typography variant="caption" color="text.secondary">—</Typography>
    },
    { key: 'customerCount', header: 'Customers', render: (v) => <Chip label={v} size="small" /> },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEditIndustry(row)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteIndustry(row.id)} disabled={row.customerCount > 0}
            title={row.customerCount > 0 ? 'Cannot delete: industry is in use' : 'Delete industry'}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const fieldTemplateColumns = [
    { key: 'fieldName', header: 'Field Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'icon', header: 'Icon', render: (v) => v || '-' },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/field-templates/${row.id}`)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteTemplate(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const profilingTemplateColumns = [
    { key: 'name', header: 'Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'description', header: 'Description', hideOnMobile: true, render: (v) => v || '-' },
    { key: 'isDefault', header: 'Default', hideOnMobile: true, render: (v) => v ? <Chip label="Default" size="small" color="primary" /> : '-' },
    { key: 'createdAt', header: 'Created', hideOnMobile: true, hideOnTablet: true, render: (v) => formatDate(v) },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/profiling-templates/${row.id}`)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteProfiling(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const productBriefColumns = [
    { key: 'name', header: 'Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'industryFocus', header: 'Industry Focus', hideOnMobile: true, render: (v) => v || '-' },
    {
      key: 'isActive', header: 'Status', render: (v, row) =>
        v
          ? <Chip label="Active" size="small" color="primary" />
          : <Button variant="outlined" size="small" onClick={() => handleSetActiveBrief(row.id)}>Set Active</Button>,
    },
    { key: 'createdAt', header: 'Created', hideOnMobile: true, hideOnTablet: true, render: (v) => formatDate(v) },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/marketing-brain/${row.id}`)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteBrief(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const signalColumns = [
    { key: 'text', header: 'Text', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'category', header: 'Category', hideOnMobile: true, render: (v) => <Chip label={v} size="small" variant="outlined" /> },
    { key: 'weight', header: 'Weight', hideOnMobile: true },
    { key: 'isActive', header: 'Active', render: (v) => v ? <Chip label="Active" size="small" color="success" /> : <Chip label="Inactive" size="small" variant="outlined" /> },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/signals/${row.id}`)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteSignal(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const aiPromptColumns = [
    { key: 'name', header: 'Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'category', header: 'Category', hideOnMobile: true, render: (v) => <Chip label={v} size="small" variant="outlined" /> },
    { key: 'isDefault', header: 'Default', hideOnMobile: true, render: (v) => v ? <Chip label="Default" size="small" color="primary" /> : '-' },
    { key: 'isActive', header: 'Active', hideOnMobile: true, render: (v) => v ? <Chip label="Active" size="small" color="success" /> : <Chip label="Inactive" size="small" variant="outlined" /> },
    { key: 'version', header: 'Version', hideOnMobile: true, hideOnTablet: true, render: (v) => `v${v}` },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/ai-prompts/${row.id}`)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={async () => { if (await dialog.confirm({ title: 'Delete prompt template', message: 'Delete this prompt template?', confirmText: 'Delete', danger: true })) { try { await deleteAiPromptTemplate(row.id); fetchAiPromptTemplates(); } catch { setError('Failed to delete prompt template'); } } }}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const interactionTemplateColumns = [
    { key: 'name', header: 'Name', render: (v) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography> },
    { key: 'channel', header: 'Channel', hideOnMobile: true, render: (v) => <Chip label={v} size="small" variant="outlined" /> },
    { key: 'stage', header: 'Stage', hideOnMobile: true, render: (v) => v || '-' },
    { key: 'language', header: 'Language', hideOnMobile: true, hideOnTablet: true, render: (v) => v || 'en' },
    { key: 'version', header: 'Version', hideOnMobile: true, hideOnTablet: true, render: (v) => `v${v || 1}` },
    {
      key: 'isActive', header: 'Status', render: (v) =>
        <Chip label={v ? 'Active' : 'Inactive'} size="small" color={v ? 'success' : 'default'} variant={v ? 'filled' : 'outlined'} />,
    },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={0.5}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/management/interaction-templates/${row.id}`)}>Edit</Button>
          <Button variant="outlined" size="small" onClick={() => handleNewVersion(row.id)}>New Ver</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDeleteInteractionTemplate(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Management</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* USERS TAB */}
      {activeTab === 'users' && (
        <>
          {users.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No users found</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddUser}>Add First User</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={userColumns}
              rows={users}
              keyField="id"
              title="User Accounts"
              actionColumnKey="actions"
              onAdd={openAddUser}
            />
          )}
        </>
      )}

      {/* INDUSTRY GROUPS TAB */}
      {activeTab === 'industry-groups' && <IndustryGroupsTab />}

      {/* INDUSTRIES TAB */}
      {activeTab === 'industries' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage the list of industries available when creating or editing customers. Industries in use cannot be deleted.
          </Typography>
          {industryList.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No industries defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddIndustry}>Add First Industry</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={industryColumns}
              rows={industryList}
              keyField="id"
              title="Industries"
              actionColumnKey="actions"
              onAdd={openAddIndustry}
            />
          )}
        </>
      )}

      {/* FIELD TEMPLATES TAB */}
      {activeTab === 'fields' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the types of additional contact information that can be added to contact persons (e.g., LinkedIn, WhatsApp, Mobile Phone).
          </Typography>
          {templates.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No field templates defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/field-templates/new')}>Add First Template</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={fieldTemplateColumns}
              rows={templates}
              keyField="id"
              title="Contact Info Field Templates"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/field-templates/new')}
            />
          )}
        </>
      )}

      {/* PROFILING TEMPLATES TAB */}
      {activeTab === 'profiling' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the profile schema used when profiling customers. The default template is applied automatically to new customers.
          </Typography>
          {profilingTemplates.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No profiling templates defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/profiling-templates/new')}>Add First Template</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={profilingTemplateColumns}
              rows={profilingTemplates}
              keyField="id"
              title="Profiling Templates"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/profiling-templates/new')}
            />
          )}
        </>
      )}

      {/* MARKETING BRAIN TAB */}
      {activeTab === 'briefs' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Product briefs provide context for all AI operations — scoring, messaging, and enrichment. Only one brief can be active at a time.
          </Typography>
          {productBriefs.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No product briefs defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/marketing-brain/new')}>Add First Brief</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={productBriefColumns}
              rows={productBriefs}
              keyField="id"
              title="Marketing Brain — Product Briefs"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/marketing-brain/new')}
            />
          )}
        </>
      )}

      {/* SIGNALS TAB */}
      {activeTab === 'signals' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Signals are observable indicators that help AI identify and score leads. Higher weight = stronger signal of fit.
          </Typography>
          {signals.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No signals defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/signals/new')}>Add First Signal</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={signalColumns}
              rows={signals}
              keyField="id"
              title="Signals"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/signals/new')}
            />
          )}
        </>
      )}

      {/* AI PROMPTS TAB */}
      {activeTab === 'prompts' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prompt templates control how AI behaves for each operation — search, enrichment, outreach, and next steps. Mark one template per category as default.
          </Typography>
          {aiPromptTemplates.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No prompt templates found</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/ai-prompts/new')}>Add First Template</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={aiPromptColumns}
              rows={aiPromptTemplates}
              keyField="id"
              title="AI Prompt Templates"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/ai-prompts/new')}
            />
          )}
        </>
      )}

      {/* INTERACTION TEMPLATES TAB */}
      {activeTab === 'interaction-templates' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Templates for lead email and LinkedIn interactions. Use {'{{placeholder}}'} syntax for dynamic values.
          </Typography>
          {interactionTemplates.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>No interaction templates defined</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/management/interaction-templates/new')}>Add First Template</Button>
              </Box>
            </Paper>
          ) : (
            <AzotteGrid
              columns={interactionTemplateColumns}
              rows={interactionTemplates}
              keyField="id"
              title="Interaction Templates"
              actionColumnKey="actions"
              onAdd={() => navigate('/management/interaction-templates/new')}
            />
          )}
        </>
      )}

      {/* SYSTEM EMAIL TEMPLATES TAB (transactional — demo / exchange) */}
      {activeTab === 'email-templates' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Transactional templates the system sends automatically (demo / exchange confirmations, reschedules, cancellations — to prospect and to host). Click a template to edit its subject and body. Use {'{{placeholder}}'} syntax for dynamic values.
          </Typography>
          {systemEmailTemplates.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary">No system email templates found. They are normally seeded on first boot.</Typography>
              </Box>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {systemEmailTemplates.map((t) => (
                <Paper
                  key={t.key}
                  sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                  onClick={() => navigate(`/management/email-templates/${encodeURIComponent(t.key)}`)}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                        {!t.isActive && <Chip label="Inactive" size="small" color="default" />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{t.key}</Typography>
                      {t.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t.description}</Typography>}
                    </Box>
                    <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/management/email-templates/${encodeURIComponent(t.key)}`); }}>
                      Edit
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* DEMO/EXCHANGE BOOKINGS TAB */}
      {activeTab === 'demo-bookings' && (
        <DemoBookingsPage />
      )}

      {/* APPOINTMENT TYPES TAB */}
      {activeTab === 'appointment-types' && (
        <AppointmentTypesPage />
      )}

      {/* LOGS TAB */}
      {activeTab === 'logs' && (
        <LogsPage />
      )}

      {/* EMAIL SETTINGS TAB */}
      {activeTab === 'email-settings' && (
        <Box>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Active Email Provider</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick which configured provider sends outbound mail. Switching is instant.
            </Typography>
            <TextField
              select
              label="Active Provider"
              size="small"
              value={activeProvider}
              onChange={(e) => handleChangeActiveProvider(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="smtp">SMTP</MenuItem>
              <MenuItem value="brevo">Brevo (HTTP API)</MenuItem>
            </TextField>

            {emailSettingsTestResult && (
              <Alert severity={emailSettingsTestResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                {emailSettingsTestResult.message}
              </Alert>
            )}
          </Paper>

          {/* SMTP CARD */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">SMTP</Typography>
                {activeProvider === 'smtp' && <Chip label="Active" size="small" color="primary" />}
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generic SMTP relay. Use for Gmail, Office 365, or any host:port + auth provider.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="SMTP Host" fullWidth size="small"
                  value={smtpForm.host}
                  onChange={e => setSmtpForm({ ...smtpForm, host: e.target.value })}
                  placeholder="e.g. smtp.gmail.com" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Port" type="number" fullWidth size="small"
                  value={smtpForm.port}
                  onChange={e => setSmtpForm({ ...smtpForm, port: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Username" fullWidth size="small"
                  value={smtpForm.username}
                  onChange={e => setSmtpForm({ ...smtpForm, username: e.target.value })}
                  placeholder="SMTP username" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Password" type="password" fullWidth size="small"
                  value={smtpForm.password}
                  onChange={e => setSmtpForm({ ...smtpForm, password: e.target.value })}
                  placeholder="Leave blank to keep existing" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="From Address" type="email" fullWidth size="small"
                  value={smtpForm.fromAddress}
                  onChange={e => setSmtpForm({ ...smtpForm, fromAddress: e.target.value })}
                  placeholder="noreply@company.com" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="From Name" fullWidth size="small"
                  value={smtpForm.fromName}
                  onChange={e => setSmtpForm({ ...smtpForm, fromName: e.target.value })}
                  placeholder="Company Name" />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Switch checked={smtpForm.useSsl}
                  onChange={e => setSmtpForm({ ...smtpForm, useSsl: e.target.checked })} />}
                label="Use SSL/TLS"
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => handleSaveProvider('smtp')}
                disabled={emailSavingProvider === 'smtp'}>
                {emailSavingProvider === 'smtp' ? 'Saving...' : 'Save SMTP'}
              </Button>
              <Button variant="outlined" onClick={() => handleTestProvider('smtp')}
                disabled={emailTestingProvider === 'smtp'}>
                {emailTestingProvider === 'smtp' ? 'Testing...' : 'Test Connection'}
              </Button>
            </Stack>
          </Paper>

          {/* BREVO CARD */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">Brevo</Typography>
                {activeProvider === 'brevo' && <Chip label="Active" size="small" color="primary" />}
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Brevo transactional HTTP API. Free tier: 300 emails/day. Get API key at brevo.com → SMTP & API → API Keys (v3).
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField label="API Key" type="password" fullWidth size="small"
                  value={brevoForm.apiKey}
                  onChange={e => setBrevoForm({ ...brevoForm, apiKey: e.target.value })}
                  placeholder={
                    emailProviders.find(p => (p.providerName || '').toLowerCase() === 'brevo')?.hasApiKey
                      ? 'Leave blank to keep existing key'
                      : 'xkeysib-...'
                  }
                  slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="From Address" type="email" fullWidth size="small"
                  value={brevoForm.fromAddress}
                  onChange={e => setBrevoForm({ ...brevoForm, fromAddress: e.target.value })}
                  placeholder="noreply@yourdomain.com (must be verified in Brevo)" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="From Name" fullWidth size="small"
                  value={brevoForm.fromName}
                  onChange={e => setBrevoForm({ ...brevoForm, fromName: e.target.value })}
                  placeholder="Company Name" />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => handleSaveProvider('brevo')}
                disabled={emailSavingProvider === 'brevo'}>
                {emailSavingProvider === 'brevo' ? 'Saving...' : 'Save Brevo'}
              </Button>
              <Button variant="outlined" onClick={() => handleTestProvider('brevo')}
                disabled={emailTestingProvider === 'brevo'}>
                {emailTestingProvider === 'brevo' ? 'Testing...' : 'Test Connection'}
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* AI PROVIDERS TAB */}
      {activeTab === 'ai' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>AI Provider Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure AI providers and integrations. An API key is required to enable each service.
          </Typography>

          {aiSettings ? (
            <form onSubmit={handleSaveAiSettings}>
              {aiSettings.providers?.map((provider) => (
                <Paper key={provider.name} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{provider.displayName}</Typography>
                      {provider.isConfigured
                        ? <Chip label="Connected" size="small" color="success" />
                        : <Chip label="Not Configured" size="small" variant="outlined" />}
                    </Stack>
                    <Button variant="outlined" size="small"
                      onClick={provider.name === 'hunterio' ? handleTestHunterIo : () => handleTestAiProvider(provider.name)}
                      disabled={aiTesting}>
                      {aiTesting ? 'Testing...' : 'Test'}
                    </Button>
                  </Stack>
                  {provider.name === 'hunterio' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Email discovery, verification, and <strong>company enrichment</strong> service — used as the first data source in the enrichment pipeline.
                    </Typography>
                  )}
                  {provider.name !== 'hunterio' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      AI provider for lead search, enrichment, outreach generation, contact discovery, and next-step suggestions.
                    </Typography>
                  )}

                  <TextField
                    label="API Key"
                    type="password"
                    fullWidth
                    size="small"
                    value={aiForm[provider.name]?.apiKey || ''}
                    onChange={(e) => setAiForm({ ...aiForm, [provider.name]: { ...aiForm[provider.name], apiKey: e.target.value } })}
                    placeholder={provider.name === 'hunterio' ? 'Enter your Hunter.io API key' : 'Enter your API key (e.g. sk-ant-...)'}
                    helperText={provider.isConfigured ? `Current key: ${provider.apiKey}` : 'No API key set'}
                    slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                    sx={{ mb: 2 }}
                  />

                  {provider.name !== 'hunterio' && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Model"
                          select
                          fullWidth
                          size="small"
                          value={aiForm[provider.name]?.model || ''}
                          onChange={(e) => setAiForm({ ...aiForm, [provider.name]: { ...aiForm[provider.name], model: e.target.value } })}
                        >
                          {(aiForm[provider.name]?.availableModels || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Max Tokens"
                          type="number"
                          fullWidth
                          size="small"
                          slotProps={{ htmlInput: { min: 512, max: 32768 } }}
                          value={aiForm[provider.name]?.maxTokens || 4096}
                          onChange={(e) => setAiForm({ ...aiForm, [provider.name]: { ...aiForm[provider.name], maxTokens: e.target.value } })}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <TextField
                      label="Enrichment Pipeline Order"
                      type="number"
                      size="small"
                      slotProps={{ htmlInput: { min: 0, max: 10 } }}
                      sx={{ width: 120 }}
                      value={aiForm[provider.name]?.enrichmentOrder || 0}
                      onChange={(e) => setAiForm({ ...aiForm, [provider.name]: { ...aiForm[provider.name], enrichmentOrder: e.target.value } })}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Lower number = higher priority in enrichment pipeline. Set 0 to exclude from enrichment.
                    </Typography>
                  </Stack>
                </Paper>
              ))}

              {aiTestResult && (
                <Alert severity={aiTestResult.success ? 'success' : 'error'} sx={{ my: 2 }}>
                  {aiTestResult.message}
                </Alert>
              )}

              {/* Default Contact Search Providers */}
              {aiSettings?.providers?.some(p => p.isConfigured) && (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Default Contact Search Providers</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Select which providers are checked by default when searching for contacts on lead and prospect pages. Multiple providers can be selected — their results will be merged.
                  </Typography>
                  <FormGroup row>
                    {aiSettings.providers.filter(p => p.isConfigured).map(p => (
                      <FormControlLabel
                        key={p.name}
                        label={p.displayName}
                        control={
                          <Checkbox
                            size="small"
                            checked={defaultContactProviders.includes(p.name)}
                            onChange={e => setDefaultContactProviders(prev =>
                              e.target.checked ? [...prev, p.name] : prev.filter(n => n !== p.name)
                            )}
                          />
                        }
                      />
                    ))}
                  </FormGroup>
                </Paper>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={aiSaving}>
                  {aiSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Stack>
            </form>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">Loading AI settings...</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* USER MODAL */}
      {userModal && (
        <Modal title={userModal === 'add' ? 'Add User' : 'Edit User'} onClose={() => setUserModal(null)}>
          <form onSubmit={handleSaveUser}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Username" required fullWidth size="small" value={userForm.username || ''} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Full Name" fullWidth size="small" value={userForm.fullName || ''} onChange={(e) => setUserForm({ ...userForm, fullName: e.target.value })} />
              </Grid>
            </Grid>
            <TextField label="Email" type="email" required fullWidth size="small" value={userForm.email || ''} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} sx={{ mt: 2 }} />
            <TextField label="Job Title (used as {{SenderTitle}})" fullWidth size="small" value={userForm.jobTitle || ''} onChange={(e) => setUserForm({ ...userForm, jobTitle: e.target.value })} sx={{ mt: 2 }} placeholder="e.g. Sales Manager" />
            <TextField
              label="Email Signature (used as {{SenderSignature}}, auto-appended if not present)"
              fullWidth
              multiline
              rows={6}
              size="small"
              value={userForm.emailSignature || ''}
              onChange={(e) => setUserForm({ ...userForm, emailSignature: e.target.value })}
              sx={{ mt: 2 }}
              placeholder="HTML or plain text. Example: <p>--<br>Jane Doe<br>Sales Manager<br>jane@company.com</p>"
              slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
            />
            {userModal === 'add' && (
              <TextField label="Password" type="password" required fullWidth size="small" slotProps={{ htmlInput: { minLength: 8 } }} value={userForm.password || ''} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} sx={{ mt: 2 }} />
            )}
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Role" select fullWidth size="small" value={userForm.role || 'User'} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                  {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              {userModal !== 'add' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Status" select fullWidth size="small" value={userForm.isActive ? 'active' : 'inactive'} onChange={(e) => setUserForm({ ...userForm, isActive: e.target.value === 'active' })}>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setUserModal(null)}>Cancel</Button>
              <Button type="submit" variant="contained">{userModal === 'add' ? 'Add User' : 'Save Changes'}</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* PASSWORD RESET MODAL */}
      {passwordModal && (
        <Modal title={`Reset Password - ${passwordModal.username}`} onClose={() => setPasswordModal(null)}>
          <form onSubmit={handleResetPassword}>
            <TextField label="New Password" type="password" required fullWidth size="small" slotProps={{ htmlInput: { minLength: 8 } }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setPasswordModal(null)}>Cancel</Button>
              <Button type="submit" variant="contained">Reset Password</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* INDUSTRY MODAL */}
      {industryModal && (
        <Modal title={industryModal === 'add' ? 'Add Industry' : 'Edit Industry'} onClose={() => setIndustryModal(null)}>
          <form onSubmit={handleSaveIndustry}>
            <Stack spacing={2}>
              <TextField label="Industry Name" required fullWidth size="small" value={industryForm.name || ''} onChange={(e) => setIndustryForm({ ...industryForm, name: e.target.value })} placeholder="e.g. Technology, Manufacturing, Finance" />
              <Autocomplete
                size="small"
                options={industryGroupOptions}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v?.id : v)}
                value={industryGroupOptions.find((g) => g.id === industryForm.industryGroupId) || null}
                onChange={(_, v) => setIndustryForm({ ...industryForm, industryGroupId: v?.id ?? null })}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {option.color && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: option.color, border: '1px solid', borderColor: 'divider' }} />
                      )}
                      <Typography variant="body2">{option.name}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">— {option.description}</Typography>
                      )}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Industry Group" placeholder="Select or search…" helperText="Optional. Templates and segmentations target groups." />
                )}
              />
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button variant="outlined" onClick={() => setIndustryModal(null)}>Cancel</Button>
              <Button type="submit" variant="contained">{industryModal === 'add' ? 'Add Industry' : 'Save Changes'}</Button>
            </DialogActions>
          </form>
        </Modal>
      )}
    </Box>
  );
}